import type { PartnerLogoUpload, VillagePartner } from '@kk/domain';
import { Card, Text, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useClients, useContentCan } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useErrorMessage, useVillageMutation, useVillagePartners } from '@/data/hooks';
import { apiBaseUrl } from '@/lib/env';

const c = deck.vsq.partners;

/**
 * **VILLAGE SQUARE'S PARTNERS** (P35's notes). Anyone on the team reads; only an owner adds, changes and approves.
 * A partner's cards go live only once it is approved and counsel has checked its wording.
 */
export function VillagePartnersScreen() {
  const partners = useVillagePartners();
  const errorMessage = useErrorMessage();
  const owner = useContentCan('team');
  const { data } = useClients();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const add = useVillageMutation(() => data.createVillagePartner({ name, referralCode: code }));
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
      </div>
      {owner ? (
        <Card>
          <form
            className="stack"
            data-testid="vsp-add"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(undefined, { onSuccess: () => (setName(''), setCode('')) });
            }}
          >
            <label className="stack">
              <span className="field-label">{c.name}</span>
              <input
                className="glyph-search"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="vsp-name"
              />
            </label>
            <label className="stack">
              <span className="field-label">{c.code}</span>
              <input
                className="glyph-search"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid="vsp-code"
              />
            </label>
            {add.error ? <Status testID="vsp-add-error">{errorMessage(add.error)}</Status> : null}
            <WasabiButton
              label={c.add}
              size="sm"
              fullWidth={false}
              disabled={!name.trim() || !code.trim()}
              onPress={() => add.mutate(undefined, { onSuccess: () => (setName(''), setCode('')) })}
              testID="vsp-add-submit"
            />
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          </form>
        </Card>
      ) : (
        <Status testID="vsp-owners-only">{c.ownersOnly}</Status>
      )}
      {partners.error ? <Status testID="vsp-error">{errorMessage(partners.error)}</Status> : null}
      <div className="stack">
        {(partners.data ?? []).map((p) => (
          <PartnerCard key={p.id} p={p} owner={owner} />
        ))}
      </div>
    </>
  );
}

function PartnerCard({ p, owner }: { p: VillagePartner; owner: boolean }) {
  const { data } = useClients();
  const errorMessage = useErrorMessage();
  const [disclosures, setDisclosures] = useState(p.disclosures.join('\n'));
  const update = useVillageMutation((input: Parameters<typeof data.updateVillagePartner>[1]) =>
    data.updateVillagePartner(p.id, input),
  );
  const approve = useVillageMutation(() => data.approveVillagePartner(p.id));
  const upload = useVillageMutation((file: PartnerLogoUpload) =>
    data.uploadVillagePartnerLogo(p.id, file),
  );
  const approveLogo = useVillageMutation(() => data.approveVillagePartnerLogo(p.id));
  const failed = update.error ?? approve.error ?? upload.error ?? approveLogo.error;
  const preview = p.logo?.images.find((i) => i.height === 56);
  return (
    <Card>
      <div className="stack" data-testid={`vsp-${p.id}`}>
        <div className="row-between">
          <Text variant="labelStrong">{p.name}</Text>
          <span className="pill" data-testid={`vsp-approved-${p.id}`}>
            {p.approvedAt ? c.approved : c.notApproved}
          </span>
        </div>
        <Text variant="bodySm" tone="muted">{`${c.code}: ${p.referralCode}`}</Text>
        <div className="stack" data-testid={`vsp-logo-${p.id}`}>
          <span className="field-label">{c.logo}</span>
          {preview ? (
            <img
              src={`${apiBaseUrl()}${preview.url}`}
              alt={p.name}
              width={preview.width / 2}
              height={28}
              data-testid={`vsp-logo-img-${p.id}`}
            />
          ) : null}
          {p.logo ? (
            <Text variant="bodySm" tone="muted" testID={`vsp-logo-state-${p.id}`}>
              {p.logo.approved ? c.logoApproved : c.logoWaiting}
            </Text>
          ) : null}
          {owner ? (
            <div className="actions">
              <label className="stack">
                <span className="field-label">{c.uploadLogo}</span>
                <input
                  type="file"
                  accept="image/png,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void fileToUpload(file).then((u) => upload.mutate(u));
                  }}
                  data-testid={`vsp-logo-file-${p.id}`}
                />
              </label>
              {p.logo && !p.logo.approved ? (
                <WasabiButton
                  label={c.approveLogo}
                  size="sm"
                  fullWidth={false}
                  onPress={() => approveLogo.mutate(undefined)}
                  testID={`vsp-logo-approve-${p.id}`}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        <label className="actions">
          <input
            type="checkbox"
            checked={p.paysFee}
            disabled={!owner}
            onChange={(e) => update.mutate({ paysFee: e.target.checked })}
            data-testid={`vsp-fee-${p.id}`}
          />
          <span>{c.paysFee}</span>
        </label>
        <label className="actions">
          <input
            type="checkbox"
            checked={p.counselChecked}
            disabled={!owner}
            onChange={(e) => update.mutate({ counselChecked: e.target.checked })}
            data-testid={`vsp-counsel-${p.id}`}
          />
          <span>{c.counselChecked}</span>
        </label>
        <label className="stack">
          <span className="field-label">{c.disclosures}</span>
          <textarea
            className="script-text"
            value={disclosures}
            disabled={!owner}
            onChange={(e) => setDisclosures(e.target.value)}
            data-testid={`vsp-disclosures-${p.id}`}
          />
        </label>
        {owner ? (
          <div className="actions">
            <LinkButton
              label={c.save}
              onPress={() => update.mutate({ disclosures: disclosures.split('\n') })}
              testID={`vsp-save-${p.id}`}
            />
            {!p.approvedAt ? (
              <WasabiButton
                label={c.approve}
                size="sm"
                fullWidth={false}
                onPress={() => approve.mutate(undefined)}
                testID={`vsp-approve-${p.id}`}
              />
            ) : null}
          </div>
        ) : null}
        {failed ? <Status testID={`vsp-error-${p.id}`}>{errorMessage(failed)}</Status> : null}
      </div>
    </Card>
  );
}

/** A chosen file as the upload the API takes: its type, and its bytes as base64. */
export async function fileToUpload(file: Blob & { type: string }): Promise<PartnerLogoUpload> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return { contentType: file.type as PartnerLogoUpload['contentType'], data: btoa(binary) };
}
